import { createCallchainHttp } from './callchain-http.js'

const WAITLIST_QUERY_KEYS = new Set([
  'status',
  'source',
  'use_case',
  'q',
  'cursor',
  'limit',
])

let http = createUnconfiguredHttp()

/**
 * @param {Parameters<typeof createCallchainHttp>[0]} opts
 */
export function configureAdminWaitlistHttp(opts) {
  http = createCallchainHttp({ ...opts, basePath: '/api' })
}

/**
 * @callchain.routeId waitlist.admin.list
 * @param {Record<string, string|number|boolean|null|undefined>} query
 */
export async function listWaitlistEntries(query) {
  assertAllowedQuery(query)
  return http.get('/admin/visual-game/waitlist', {
    query,
    routeId: 'waitlist.admin.list',
  })
}

/**
 * @callchain.routeId waitlist.admin.detail
 * @param {number} id
 */
export async function getWaitlistEntry(id) {
  return http.get(`/admin/visual-game/waitlist/${encodeURIComponent(id)}`, {
    routeId: 'waitlist.admin.detail',
  })
}

/**
 * @callchain.routeId waitlist.admin.create
 * @param {Record<string, unknown>} input
 */
export async function createManualWaitlistEntry(input) {
  return http.post('/admin/visual-game/waitlist', {
    body: input,
    routeId: 'waitlist.admin.create',
  })
}

export async function createManualInvite(input) {
  return http.post('/admin/visual-game/invitation-codes', {
    body: input,
    routeId: 'waitlist.admin.createManualInvite',
  })
}

export async function regenerateManualInvite(id) {
  return http.post(`/admin/visual-game/invitation-codes/${encodeURIComponent(id)}/regenerate`, {
    body: {},
    routeId: 'waitlist.admin.regenerateManualInvite',
  })
}

/**
 * @callchain.routeId waitlist.admin.note
 * @param {number} id
 * @param {{ note: string }} input
 */
export async function addWaitlistNote(id, input) {
  return http.post(`/admin/visual-game/waitlist/${encodeURIComponent(id)}/note`, {
    body: input,
    routeId: 'waitlist.admin.note',
  })
}

/**
 * @callchain.routeId waitlist.admin.invite
 * @param {number} id
 * @param {{ note?: string }} input
 */
export async function inviteWaitlistEntry(id, input) {
  return http.post(`/admin/visual-game/waitlist/${encodeURIComponent(id)}/invite`, {
    body: input,
    routeId: 'waitlist.admin.invite',
  })
}

/**
 * @callchain.routeId waitlist.admin.resend
 * @param {number} id
 * @param {{ note?: string }} input
 */
export async function resendWaitlistInvite(id, input) {
  return http.post(`/admin/visual-game/waitlist/${encodeURIComponent(id)}/resend`, {
    body: input,
    routeId: 'waitlist.admin.resend',
  })
}

/**
 * @callchain.routeId waitlist.admin.reject
 * @param {number} id
 * @param {{ reason: string }} input
 */
export async function rejectWaitlistEntry(id, input) {
  return http.post(`/admin/visual-game/waitlist/${encodeURIComponent(id)}/reject`, {
    body: input,
    routeId: 'waitlist.admin.reject',
  })
}

/**
 * @callchain.routeId waitlist.admin.export
 * @param {Record<string, string|number|boolean|null|undefined>} query
 */
export async function exportWaitlistEntries(query) {
  assertAllowedQuery(query)
  return http.getText('/admin/visual-game/waitlist/export', {
    query,
    routeId: 'waitlist.admin.export',
  })
}

/** @param {Record<string, unknown>} query */
function assertAllowedQuery(query) {
  for (const key of Object.keys(query)) {
    if (!WAITLIST_QUERY_KEYS.has(key)) {
      throw new Error(`Unknown waitlist query key: ${key}`)
    }
  }
}

function createUnconfiguredHttp() {
  return {
    get() {
      throw new Error('configureAdminWaitlistHttp must be called before waitlist API use')
    },
    post() {
      throw new Error('configureAdminWaitlistHttp must be called before waitlist API use')
    },
    getText() {
      throw new Error('configureAdminWaitlistHttp must be called before waitlist API use')
    },
  }
}
